import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged } from "../config/firebase";
import {
  buildWalletData,
  fetchCryptoPrices,
  getNotification,
  getUserInfo,
} from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
} from "../redux/action";

export function DashboardIndex() {
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);

  const dispatch = useDispatch();

  React.useEffect(() => {
    let latestBalances = null;
    let pricesLoaded = false;
    let userSub = null;
    let notifications = null;
    let unsubscribed = false;

    const publishWallets = () => {
      if (!latestBalances || !pricesLoaded || unsubscribed) return;
      dispatch(walletData$(buildWalletData(latestBalances)));
      setLoading(false);
    };

    // Prices fetched once — not inside the Firestore user subscription
    fetchCryptoPrices()
      .then(() => {
        if (unsubscribed) return;
        pricesLoaded = true;
        publishWallets();
      })
      .catch((error) => {
        console.log(error);
        if (unsubscribed) return;
        // Still mark loaded so cached/fallback prices can build wallets
        pricesLoaded = true;
        publishWallets();
      });

    const authUnsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        getNotification(user.uid);

        userSub = getUserInfo(user.uid).subscribe((userData) => {
          const {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          } = userData;

          const totalbalance = [
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          ].reduce((prv, cur) => prv + cur, 0);

          dispatch(
            userinfo$({
              ...userData,
              id: user.uid,
              totalBalance: totalbalance,
            })
          );

          // Rebuild wallets from cached prices only — no CoinGecko call here
          latestBalances = {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          };
          publishWallets();
        });

        const usertransactionref = query(
          collection(db, "users", user.uid, "transactions"),
          orderBy("timestamp", "desc")
        );
        notifications = collectionData(usertransactionref).subscribe(
          (data) => {
            const getAllDocsWithoutField = (fieldName) =>
              data.filter((doc) => !(fieldName in doc));

            dispatch(notificationall$(data.length));
            dispatch(notification$(getAllDocsWithoutField("seen").length));
          }
        );
      } else {
        navigate("/auth/login");
      }
    });

    return () => {
      unsubscribed = true;
      authUnsub?.();
      userSub?.unsubscribe?.();
      notifications?.unsubscribe?.();
    };
  }, [dispatch, navigate]);

  return (
    <>
      <Helmet>
        <script src="//code.jivosite.com/widget/7O1v6rDMUe" async />
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}
